// @flow
import React, { PureComponent, lazy } from 'react';
import ConnectViewer from 'app/common/react/components/common/ConnectViewer/ConnectViewer';
import cx from 'classnames';
import i18n from 'app/common/lib/i18n';
import NuxInterest from 'app/common/react/components/growth/activation/NuxInterest/NuxInterest';
import SafeSuspense from 'app/packages/safe-suspense';
import type { ProfileTopic } from 'app/packages/pinterest-flow-types/topic';
import type { Viewer } from 'app/packages/pinterest-flow-types/viewer';
import urlUtil from 'app/common/lib/urlUtil';
import withResource from 'app/common/lib/react/withResource';
import { interpolateNamedTemplate } from 'app/common/lib/react/interpolate';
import { track } from 'app/common/lib/react/analytics';
import { Box, Heading, IconButton, Spinner } from 'gestalt';

const RenuxContainer = lazy(() =>
  import(/* webpackChunkName: "RenuxContainerCommon" */ 'app/common/react/components/growth/activation/Renux/RenuxContainer'),
);

const LAST_TOPIC_INDEX = 5;

type TopicProps = ProfileTopic & {|
  label: string,
|};

type HeroProps = {|
  handleComplete: () => void,
  handleDismiss: () => void,
  updateTopicString: () => void,
  topic?: string,
  topicsResource: {|
    data: Array<TopicProps>,
    fetchMore: () => void,
    isAtEnd: boolean,
    isFetching: boolean,
    isLoaded: boolean,
    refreshData: () => void,
  |},
|};

type HeroState = {
  openRenux: boolean,
};

export class TopicEducation extends PureComponent<HeroProps, HeroState> {
  static defaultProps = {
    handleComplete: () => {},
    handleDismiss: () => {},
  };

  state = {
    openRenux: false,
  };

  handleDismiss = () => {
    this.setState(() => ({ openRenux: false }));
  };

  flipRenux = () => {
    const newState = !this.state.openRenux;
    const {
      topicsResource: { refreshData },
    } = this.props;
    if (!newState) {
      refreshData();
    } else {
      track(PCONST.thrift.EventType.CLICK, {
        component: PCONST.thrift.ComponentType.TOPIC_EDCUATION_RENUX_BUTTON, // sic
        viewParameter: PCONST.thrift.ViewParameterType.TOPIC_EDUCATION,
      });
    }

    this.setState(({ openRenux }) => ({ openRenux: !openRenux }));
  };

  renderTopics(data: Array<TopicProps>) {
    const followedTopics = data.filter((topic) => topic.is_followed);
    const truncatedTopicsList = followedTopics.slice(0, LAST_TOPIC_INDEX);
    const showOverflowMask = followedTopics.length > LAST_TOPIC_INDEX;
    const lastInterestIndex = truncatedTopicsList.length - 1;
    const overflowLabel = i18n._('+ more', 'there are other interests not shown');

    // $FlowFixMe: new errors found from flow 0.96 upgrade
    return truncatedTopicsList.map((interest, index) => {
      const newTopic = {
        ...interest,
        label: showOverflowMask && index === lastInterestIndex ? overflowLabel : interest.name,
        images: { '400x': interest.images['400x'] },
      };
      return (
        <Box key={newTopic.id} paddingX={2} display="inlineBlock">
          {/* $FlowFixMe: new errors found from flow 0.96 upgrade */}
          <NuxInterest interest={newTopic} key={newTopic.id} isTopicEdu />
        </Box>
      );
    });
  }

  render() {
    const {
      topicsResource: { data, isFetching, isLoaded },
    } = this.props;

    const className = cx('Module', 'HeroBasic', 'white');

    return (
      <div className={className}>
        <Box
          height={80}
          alignItems="start"
          direction="row"
          display="flex"
          justifyContent="center"
          paddingX={12}
          paddingY={6}
        >
          <Box paddingX={12}>
            <Heading size="xs">
              <ConnectViewer>
                {(viewer: Viewer) =>
                  interpolateNamedTemplate(
                    i18n._(
                      'Hi {{firstName}}! Your feed is made up of these topics',
                      'Educating user that these are the topics they picked',
                    ),
                    { firstName: viewer.firstName || '' },
                  )
                }
              </ConnectViewer>
            </Heading>
          </Box>
        </Box>
        <Box
          alignItems="start"
          direction="row"
          justifyContent="center"
          display="flex"
          marginBottom={8}
        >
          {isLoaded && data.length ? this.renderTopics(data) : null}
          <Box alignItems="center" direction="row" display="flex" justifyContent="center">
            <Spinner
              accessibilityLabel={i18n._(
                'Loading additional topics',
                'Label for the loading state spinner in the Topics section of the Following page',
              )}
              show={isFetching || !isLoaded}
            />
          </Box>
          <Box display="flex" alignItems="center" direction="column" alignSelf="center">
            <Box paddingX={2} display="inlineBlock">
              <IconButton
                size="xl"
                bgColor="lightGray"
                icon="edit"
                iconColor="gray"
                accessibilityLabel="Modify Topics"
                onClick={this.flipRenux}
              />
              <SafeSuspense>
                {this.state.openRenux && (
                  <RenuxContainer
                    isSimpleRenux
                    dismissCallback={this.flipRenux}
                    completeCallback={this.flipRenux}
                  />
                )}
              </SafeSuspense>
            </Box>
          </Box>
        </Box>
      </div>
    );
  }
}

export default withResource({
  name: 'NuxInterestsResource',
  key: 'topicsResource',
  options: {
    is_renux: true,
    isLazyLoading: true,
    urlParams: urlUtil.getQueryStringParams(),
  },
})(TopicEducation);
